import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Icon,
  Image,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Flex
} from '@chakra-ui/react';
import {
  FaBook,
  FaChalkboardTeacher,
  FaUserGraduate,
  FaQuoteLeft,
  FaCertificate,
  FaVolumeUp,
  FaVolumeOff
} from 'react-icons/fa';
import { MdSchool, MdTrendingUp } from 'react-icons/md';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const MotionBox = motion(Box);

const HomePage = () => {
  const navigate = useNavigate();
  const [isVideoMuted, setIsVideoMuted] = useState(true);

  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';

  const stats = [
    { number: '1500+', label: 'درس تعليمي', icon: FaBook },
    { number: '150+', label: 'حصة شهرياً', icon: FaChalkboardTeacher },
    { number: '20+', label: 'أستاذ متخصص', icon: FaUserGraduate },
    { number: '1200+', label: 'تلميذ ناجح', icon: MdSchool }
  ];

  const features = [
    {
      title: 'دروس مسجلة للتلاميذ',
      description: 'محتوى تعليمي شامل متوافق مع المنهج التونسي لجميع المستويات',
      icon: FaBook,
      color: primaryColor
    },
    {
      title: 'حصص مباشرة تفاعلية',
      description: 'تواصل مباشر مع الأساتذة لضمان فهم أفضل للدروس',
      icon: FaChalkboardTeacher,
      color: '#D69E2E'
    },
    {
      title: 'مواد تعليمية متنوعة',
      description: 'تمارين، ملخصات، وامتحانات لتعزيز التحصيل الدراسي',
      icon: MdSchool,
      color: '#ECC94B'
    },
    {
      title: 'متابعة تعليمية مستمرة',
      description: 'تقييم دوري لمستوى التلاميذ وتقارير تفصيلية للأولياء',
      icon: FaCertificate,
      color: '#F6AD55'
    }
  ];

  const testimonials = [
    {
      quote: "منصة تعليمية ممتازة ساعدت ابني على تحسين مستواه الدراسي بشكل ملحوظ",
      author: "السيدة ليلى م.",
      role: "ولي أمر تلميذ"
    },
    {
      quote: "الدروس واضحة والأساتذة يشرحون بطريقة سهلة الفهم، أنصح كل التلاميذ بهذه المنصة",
      author: "يوسف ك.",
      role: "تلميذ باكالوريا"
    },
    {
      quote: "الحصص المباشرة والتمارين المصححة ساعدتني كثيراً في الاستعداد للامتحانات",
      author: "أمينة س.",
      role: "تلميذة سنة رابعة"
    }
  ];

  const faqs = [
    {
      question: "كيف يمكن تسجيل تلميذ في المنصة؟",
      answer: "يمكنك التواصل معنا عبر واتساب أو الهاتف لتسجيل التلميذ والحصول على معلومات الدخول."
    },
    {
      question: "هل الدروس مناسبة لجميع المستويات التعليمية؟",
      answer: "نعم، نوفر دروساً لجميع مستويات الإعدادي والثانوي بمختلف الشعب."
    },
    {
      question: "هل يمكن للتلاميذ مراجعة الحصص المباشرة؟",
      answer: "نعم، جميع الحصص المباشرة يتم تسجيلها وتبقى متاحة للمراجعة في أي وقت."
    },
    {
      question: "هل المحتوى التعليمي متوافق مع المنهج التونسي؟",
      answer: "بالطبع، جميع دروسنا معدة وفق المنهج الرسمي لوزارة التربية التونسية."
    }
  ];

  const handleVideoClick = (event) => {
    const video = event.target;
    if (isVideoMuted) {
      video.muted = false;
      setIsVideoMuted(false);
    } else {
      video.muted = true;
      setIsVideoMuted(true);
    }
  };

  return (
    <Box dir="rtl" bg="gray.50">
      <Header />

      {/* Hero Section with Video */}
      <Box
        bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`}
        color="white"
        py={{ base: 16, md: 24 }}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="1400px" position="relative" zIndex={1}>
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            align="center"
            justify="space-between"
            gap={12}
          >
            {/* Left Side - Content */}
            <VStack align={{ base: 'center', lg: 'start' }} spacing={8} flex={1} textAlign={{ base: 'center', lg: 'right' }}>
              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Badge
                  bg={primaryColor}
                  color={secondaryColor}
                  px={6}
                  py={2}
                  borderRadius="full"
                  fontSize="md"
                  fontFamily="Tajawal, sans-serif"
                  fontWeight="700"
                >
                  منصة كوبرا التعليمية
                </Badge>
              </MotionBox>

              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Heading
                  fontSize={{ base: '4xl', md: '6xl', lg: '7xl' }}
                  fontFamily="Tajawal, sans-serif"
                  fontWeight="900"
                  lineHeight="1.2"
                  mb={4}
                >
                  التميز التعليمي
                  <br />
                  <Text as="span" color={primaryColor}>
                    لكل تلميذ
                  </Text>
                </Heading>
              </MotionBox>

              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Text
                  fontSize={{ base: 'lg', md: 'xl' }}
                  fontFamily="Tajawal, sans-serif"
                  maxW="600px"
                  lineHeight="1.8"
                  color="whiteAlpha.900"
                >
                  منصة تعليمية متكاملة للتلاميذ من الإعدادي إلى الباكالوريا
                  <br />
                  دروس، حصص مباشرة، ومتابعة تربوية مستمرة
                </Text>
              </MotionBox>

              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <HStack spacing={4} flexWrap="wrap" justify={{ base: 'center', lg: 'start' }}>
                  <Button
                    size="lg"
                    bg={primaryColor}
                    color={secondaryColor}
                    _hover={{ bg: '#E0B817', transform: 'translateY(-2px)' }}
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="800"
                    px={10}
                    py={7}
                    borderRadius="xl"
                    boxShadow="xl"
                    onClick={() => navigate('/discover')}
                  >
                    اكتشف المحتوى التعليمي
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    borderColor="white"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="700"
                    px={10}
                    py={7}
                    borderRadius="xl"
                    onClick={() => navigate('/contact')}
                  >
                    تواصل معنا
                  </Button>
                </HStack>
              </MotionBox>
            </VStack>

            {/* Right Side - Video */}
            <Box flex={1} w="100%">
              <MotionBox
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  position="relative"
                  borderRadius="2xl"
                  overflow="hidden"
                  boxShadow="2xl"
                  maxW="600px"
                  mx="auto"
                >
                  <video
                    autoPlay
                    muted={isVideoMuted}
                    loop
                    playsInline
                    onClick={handleVideoClick}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    <source src="./assets/cobra-intro.mp4" type="video/mp4" />
                    <Image
                      src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800"
                      alt="تلاميذ يتعلمون"
                      borderRadius="2xl"
                      fallbackSrc="https://ui-avatars.com/api/?name=Cobra+Education&background=F2C919&color=2C3E50&size=600"
                    />
                  </video>

                  {/* Mute/Unmute indicator */}
                  <Box
                    position="absolute"
                    bottom="4"
                    right="4"
                    bg="rgba(0,0,0,0.7)"
                    color="white"
                    p={2}
                    borderRadius="md"
                    fontSize="sm"
                    fontFamily="Tajawal, sans-serif"
                    display="flex"
                    alignItems="center"
                    cursor="pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const video = e.target.closest('div').previousElementSibling;
                      handleVideoClick({ target: video });
                    }}
                    _hover={{ bg: "rgba(0,0,0,0.9)" }}
                    transition="background-color 0.2s"
                  >
                    <Icon as={isVideoMuted ? FaVolumeOff : FaVolumeUp} mr={2} />
                    {isVideoMuted ? 'انقر للصوت' : 'انقر للكتم'}
                  </Box>
                </Box>
              </MotionBox>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="1400px">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            {stats.map((stat, index) => (
              <MotionBox
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <VStack
                  spacing={4}
                  p={6}
                  bg="gray.50"
                  borderRadius="2xl"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg', bg: `${primaryColor}10` }}
                >
                  <Icon as={stat.icon} boxSize={10} color={primaryColor} />
                  <Heading
                    fontSize="4xl"
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="900"
                    color={secondaryColor}
                  >
                    {stat.number}
                  </Heading>
                  <Text
                    fontSize="md"
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="600"
                    color="gray.600"
                  >
                    {stat.label}
                  </Text>
                </VStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={{ base: 16, md: 24 }} bg="gray.50">
        <Container maxW="1400px">
          <VStack spacing={4} mb={16} textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '5xl' }}
              fontFamily="Tajawal, sans-serif"
              fontWeight="900"
              color={secondaryColor}
            >
              خدماتنا التعليمية
            </Heading>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="gray.600"
              fontFamily="Tajawal, sans-serif"
              maxW="700px"
            >
              نوفر للتلاميذ كل ما يحتاجونه للنجاح الدراسي
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
            {features.map((feature, index) => (
              <MotionBox
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <Box
                  bg="white"
                  p={8}
                  borderRadius="2xl"
                  boxShadow="lg"
                  transition="all 0.3s"
                  _hover={{ boxShadow: '2xl' }}
                  h="100%"
                  border="2px solid"
                  borderColor="gray.100"
                >
                  <VStack align="start" spacing={4}>
                    <Box
                      bg={`${feature.color}15`}
                      p={4}
                      borderRadius="xl"
                    >
                      <Icon as={feature.icon} boxSize={8} color={feature.color} />
                    </Box>
                    <Heading
                      fontSize="xl"
                      fontFamily="Tajawal, sans-serif"
                      fontWeight="800"
                      color={secondaryColor}
                    >
                      {feature.title}
                    </Heading>
                    <Text
                      fontSize="md"
                      color="gray.600"
                      fontFamily="Tajawal, sans-serif"
                      lineHeight="1.7"
                    >
                      {feature.description}
                    </Text>
                  </VStack>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="1400px">
          <VStack spacing={4} mb={16} textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '5xl' }}
              fontFamily="Tajawal, sans-serif"
              fontWeight="900"
              color={secondaryColor}
            >
              آراء التلاميذ والأولياء
            </Heading>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="gray.600"
              fontFamily="Tajawal, sans-serif"
              maxW="700px"
            >
              تجارب ناجحة لتلاميذ حققوا التميز معنا
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {testimonials.map((testimonial, index) => (
              <MotionBox
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Box
                  bg="gray.50"
                  p={8}
                  borderRadius="2xl"
                  boxShadow="md"
                  transition="all 0.3s"
                  _hover={{ boxShadow: 'xl', transform: 'translateY(-4px)' }}
                  h="100%"
                >
                  <VStack align="start" spacing={4}>
                    <Icon as={FaQuoteLeft} boxSize={8} color={primaryColor} />
                    <Text
                      fontSize="md"
                      fontFamily="Tajawal, sans-serif"
                      color="gray.700"
                      lineHeight="1.8"
                      fontStyle="italic"
                    >
                      "{testimonial.quote}"
                    </Text>
                    <Box>
                      <Text
                        fontWeight="700"
                        color={secondaryColor}
                        fontFamily="Tajawal, sans-serif"
                        fontSize="lg"
                      >
                        {testimonial.author}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontFamily="Tajawal, sans-serif"
                      >
                        {testimonial.role}
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={{ base: 16, md: 24 }} bg="gray.50">
        <Container maxW="900px">
          <VStack spacing={4} mb={12} textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '5xl' }}
              fontFamily="Tajawal, sans-serif"
              fontWeight="900"
              color={secondaryColor}
            >
              الأسئلة الشائعة
            </Heading>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="gray.600"
              fontFamily="Tajawal, sans-serif"
            >
              إجابات على أكثر الأسئلة شيوعاً حول المنصة التعليمية
            </Text>
          </VStack>

          <Accordion allowToggle>
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                border="none"
                mb={4}
                bg="white"
                borderRadius="xl"
                overflow="hidden"
              >
                <AccordionButton
                  p={6}
                  _hover={{ bg: `${primaryColor}10` }}
                  _expanded={{ bg: primaryColor, color: secondaryColor }}
                  transition="all 0.3s"
                >
                  <Box flex="1" textAlign="right" fontWeight="700" fontFamily="Tajawal, sans-serif" fontSize="lg">
                    {faq.question}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={6} pt={4} px={6} fontFamily="Tajawal, sans-serif" color="gray.700" lineHeight="1.8">
                  {faq.answer}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`}
        color="white"
        py={{ base: 16, md: 24 }}
      >
        <Container maxW="1000px" textAlign="center">
          <VStack spacing={8}>
            <Heading
              fontSize={{ base: '3xl', md: '5xl' }}
              fontFamily="Tajawal, sans-serif"
              fontWeight="900"
            >
              ابدأ رحلة التميز التعليمي اليوم
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              fontFamily="Tajawal, sans-serif"
              maxW="700px"
              lineHeight="1.8"
            >
              انضم إلى آلاف التلاميذ الذين يحققون النجاح الدراسي مع منصة كوبرا
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button
                size="lg"
                bg={primaryColor}
                color={secondaryColor}
                _hover={{ bg: '#E0B817', transform: 'translateY(-2px)' }}
                fontFamily="Tajawal, sans-serif"
                fontWeight="800"
                px={12}
                py={7}
                borderRadius="xl"
                boxShadow="xl"
                onClick={() => navigate('/discover')}
              >
                استكشف المحتوى
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: 'whiteAlpha.200' }}
                fontFamily="Tajawal, sans-serif"
                fontWeight="700"
                px={12}
                py={7}
                borderRadius="xl"
                onClick={() => navigate('/contact')}
              >
                تواصل معنا
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default HomePage;
